import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, AlertTriangle, Info, AlertCircle, XCircle, Ticket, Server, CheckCheck } from 'lucide-react';
import apiClient from '@/lib/api';
import type { MasterServiceAlert as Alert } from '@easyflow/shared';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import io, { Socket } from 'socket.io-client';
import { getSocketUrl, getSocketConfig } from '@easyflow/ui-frontend';

interface TicketNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  priority: string;
  action_url: string | null;
  metadata: {
    ticketId?: string;
    subject?: string;
    urgency?: string;
    category?: string;
    tenantName?: string;
    creatorName?: string;
  } | null;
  read: boolean;
  created_at: string;
}

type NotificationItem =
  | { kind: 'alert'; data: Alert }
  | { kind: 'ticket'; data: TicketNotification };

const severityConfig = {
  critical: {
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  error: {
    icon: AlertCircle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
  },
  info: {
    icon: Info,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
};

const urgencyStyles: Record<string, { bg: string; text: string; label: string }> = {
  low: { bg: 'bg-green-100', text: 'text-green-700', label: 'Bassa' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Media' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Alta' },
  critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'Critica' },
};

export default function NotificationBell() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [ticketNotifications, setTicketNotifications] = useState<TicketNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);

  const loadAlerts = useCallback(async () => {
    try {
      const response = await apiClient.get<Alert[]>('/master/alerts?dismissed=false&limit=20');
      setAlerts(Array.isArray(response.data) ? response.data : []);
    } catch {
      setAlerts([]);
    }
  }, []);

  const loadTicketNotifications = useCallback(async () => {
    try {
      const response = await apiClient.get<{ notifications: TicketNotification[] }>('/master/notifications?unread_only=true&limit=20');
      if (response.success && response.data) {
        const notifs = response.data.notifications || response.data || [];
        const ticketNotifs = (Array.isArray(notifs) ? notifs : []).filter(
          (n: TicketNotification) => n.type === 'support_ticket' || n.type === 'service_alert'
        );
        setTicketNotifications(ticketNotifs);
      }
    } catch {
      setTicketNotifications([]);
    }
  }, []);

  useEffect(() => {
    void loadAlerts();
    void loadTicketNotifications();
    const interval = setInterval(() => {
      void loadAlerts();
      void loadTicketNotifications();
    }, 30000);

    const token = localStorage.getItem('token');
    if (token) {
      // Task #2569 — config Socket.IO condivisa (riconnessione infinita + jitter).
      const socket = io(getSocketUrl(), {
        ...getSocketConfig(),
        auth: { token },
      });

      socketRef.current = socket;

      socket.on('notification:new', (notification: { type?: string }) => {
        if (notification?.type === 'service_alert') {
          void loadAlerts();
        }
      });

      socket.on('connect_error', () => {});
    }

    return () => {
      clearInterval(interval);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [loadAlerts, loadTicketNotifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setExpandedItem(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = alerts.length + ticketNotifications.length;

  const allItems: NotificationItem[] = [
    ...ticketNotifications.map((n) => ({ kind: 'ticket' as const, data: n })),
    ...alerts.map((a) => ({ kind: 'alert' as const, data: a })),
  ].sort((a, b) => {
    const dateA = new Date(a.data.created_at).getTime();
    const dateB = new Date(b.data.created_at).getTime();
    return dateB - dateA;
  });

  const dismissAlert = async (alertId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await apiClient.put(`/master/alerts/${alertId}/dismiss`);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  const dismissTicketNotification = async (notifId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await apiClient.put(`/master/notifications/${notifId}/read`);
      setTicketNotifications((prev) => prev.filter((n) => n.id !== notifId));
    } catch (error) {
      console.error('Failed to dismiss ticket notification:', error);
    }
  };

  const dismissAllAlerts = async () => {
    try {
      await apiClient.put('/master/alerts/dismiss-all');
      setAlerts([]);
    } catch (error) {
      console.error('Failed to dismiss all alerts:', error);
    }
  };

  const markAllAsRead = async () => {
    await dismissAllAlerts();
    const promises = ticketNotifications.map((n) =>
      apiClient.put(`/master/notifications/${n.id}/read`).catch(() => {})
    );
    await Promise.all(promises);
    setTicketNotifications([]);
    setExpandedItem(null);
  };

  const handleAlertClick = (alert: Alert) => {
    const itemKey = `alert-${alert.id}`;
    if (expandedItem === itemKey) {
      void dismissAlert(alert.id);
      setExpandedItem(null);
    } else {
      setExpandedItem(itemKey);
    }
  };

  const handleTicketNotificationClick = (notif: TicketNotification) => {
    const itemKey = `ticket-${notif.id}`;
    if (expandedItem === itemKey) {
      void dismissTicketNotification(notif.id);
      setExpandedItem(null);
    } else {
      setExpandedItem(itemKey);
    }
  };

  const getAlertIcon = (alert: Alert) => {
    if (alert.category === 'infrastructure') {
      return Server;
    }
    return severityConfig[alert.severity]?.icon || Info;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setIsOpen(!isOpen); if (isOpen) setExpandedItem(null); }}
        className="relative p-2 rounded-lg hover:bg-muted dark:hover:bg-white/10 transition-colors"
      >
        <Bell className="h-5 w-5 text-muted-foreground dark:text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-semibold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-12 w-96 max-h-[500px] flex flex-col bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border border-white/20 z-50">
          <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
            <h3 className="font-semibold text-foreground">Notifiche</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    {unreadCount} non lette
                  </span>
                  <button
                    onClick={markAllAsRead}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-blue-600 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                    title="Segna tutte come lette"
                  >
                    <CheckCheck className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {allItems.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-2 opacity-20" />
                <p className="text-sm">Nessuna nuova notifica</p>
              </div>
            ) : (
              <div>
                {allItems.map((item) => {
                  if (item.kind === 'ticket') {
                    const notif = item.data;
                    const urgency = notif.metadata?.urgency || 'medium';
                    const urg = urgencyStyles[urgency] ?? urgencyStyles.medium ?? { bg: '', text: '', label: urgency };
                    const isExpanded = expandedItem === `ticket-${notif.id}`;

                    return (
                      <div
                        key={`ticket-${notif.id}`}
                        className={`p-4 border-b border-gray-100 hover:bg-muted/50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/50' : ''}`}
                        onClick={() => handleTicketNotificationClick(notif)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                            <Ticket className="h-4 w-4 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-semibold text-sm text-foreground">{notif.title}</p>
                              <button
                                onClick={(e) => dismissTicketNotification(notif.id, e)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title="Segna come letta"
                              >
                                <X className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </div>
                            {notif.body && (
                              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{notif.body}</p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                              {notif.metadata?.tenantName && (
                                <span className="bg-muted text-foreground px-2 py-0.5 rounded">
                                  {notif.metadata.tenantName}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded font-semibold ${urg.bg} ${urg.text}`}>
                                {urg.label}
                              </span>
                              <span className="text-muted-foreground/70">
                                {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: it })}
                              </span>
                            </div>
                            {isExpanded && (
                              <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
                                Clicca di nuovo per segnare come letta
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const alert = item.data;
                  const config = severityConfig[alert.severity];
                  const Icon = getAlertIcon(alert);
                  const isExpanded = expandedItem === `alert-${alert.id}`;

                  return (
                    <div
                      key={`alert-${alert.id}`}
                      className={`p-4 border-b border-gray-100 hover:bg-muted/50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/50' : ''}`}
                      onClick={() => handleAlertClick(alert)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`${config.bgColor} p-2 rounded-lg flex-shrink-0`}>
                          <Icon className={`h-4 w-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="font-semibold text-sm text-foreground">{alert.title}</p>
                            <button
                              onClick={(e) => dismissAlert(alert.id, e)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                              title="Segna come letta"
                            >
                              <X className="h-3 w-3 text-muted-foreground" />
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{alert.message}</p>
                          {isExpanded && alert.category === 'infrastructure' && alert.metadata && (
                            <div className="mb-2 text-xs bg-muted/50 rounded p-2 space-y-1">
                              {alert.metadata.server && (
                                <div className="flex gap-2">
                                  <span className="text-muted-foreground font-medium">Server:</span>
                                  <span className="text-foreground">{alert.metadata.server}{alert.metadata.host ? ` (${alert.metadata.host})` : ''}</span>
                                </div>
                              )}
                              {alert.metadata.service && (
                                <div className="flex gap-2">
                                  <span className="text-muted-foreground font-medium">Servizio:</span>
                                  <span className="text-foreground">{alert.metadata.service}</span>
                                </div>
                                )}
                              {alert.metadata.suggestion && (
                                <div className="flex gap-2">
                                  <span className="text-muted-foreground font-medium">Azione:</span>
                                  <span className="text-foreground">{alert.metadata.suggestion}</span>
                                </div>
                              )}
                              {alert.metadata.eventType === 'recovery' && (
                                <span className="inline-block bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">
                                  RECUPERATO
                                </span>
                              )}
                            </div>
                          )}
                          {!isExpanded && alert.category === 'infrastructure' && alert.metadata && (
                            <div className="mb-2">
                              {alert.metadata.eventType === 'recovery' && (
                                <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">
                                  RECUPERATO
                                </span>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-2 flex-wrap text-xs">
                            {alert.category === 'infrastructure' && (
                              <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-semibold">
                                Infrastruttura
                              </span>
                            )}
                            {alert.tenant_name && (
                              <span className="bg-muted text-foreground px-2 py-0.5 rounded">
                                {alert.tenant_name}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded uppercase font-semibold ${
                              alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                              alert.severity === 'error' ? 'bg-orange-100 text-orange-700' :
                              alert.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {alert.severity}
                            </span>
                            <span className="text-muted-foreground/70">
                              {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true, locale: it })}
                            </span>
                          </div>
                          {isExpanded && (
                            <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
                              Clicca di nuovo per segnare come letta
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
